import { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray, Controller, type Control, type UseFormRegister } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/Spinner';
import { JacketUpload } from '@/components/JacketUpload';
import { JacketAttachments } from '@/components/JacketAttachments';
import { LenderAutocomplete } from '@/components/LenderAutocomplete';
import type { JacketFormValues } from '@/types/api';

const OVER_LIMIT = 1_000_000;

const mortgageSchema = z.object({
  date: z.string(), formTypeId: z.coerce.number(), amount: z.coerce.number(),
  grossPremium: z.coerce.number(), netPremium: z.coerce.number(),
  loanNumber: z.string(), lenderName: z.string(), borrowerName: z.string(),
});
const endorsementSchema = z.object({ id: z.coerce.number(), name: z.string(), price: z.coerce.number() });

const schema = z.object({
  fileNumber: z.string().min(1, 'File number is required'),
  policyType: z.string().min(1, 'Select a jacket type'),
  propertyType: z.string(),
  address: z.string(), city: z.string(), county: z.string(), state: z.string(), zip: z.string(),
  loan: z.object({
    effectiveDate: z.string(), formTypeId: z.coerce.number(), liabilityAmount: z.coerce.number(),
    overLimitApproval: z.boolean(), grossPremium: z.coerce.number(), netPremium: z.coerce.number(),
    lenderName: z.string(), borrowerName: z.string(), scheduleB: z.string(), loanNumber: z.string(),
  }),
  owner: z.object({
    effectiveDate: z.string(), formTypeId: z.coerce.number(), liabilityAmount: z.coerce.number(),
    overLimitApproval: z.boolean(), grossPremium: z.coerce.number(), netPremium: z.coerce.number(),
  }),
  commitment: z.object({
    effectiveDate: z.string(), formTypeId: z.coerce.number(), liabilityAmount: z.coerce.number(), grossPremium: z.coerce.number(),
  }),
  mortgages: z.array(mortgageSchema),
  endorsements: z.array(endorsementSchema),
});
type Values = z.infer<typeof schema>;

const EMPTY: Values = {
  fileNumber: '', policyType: '', propertyType: 'Residential',
  address: '', city: '', county: 'Oklahoma', state: 'OK', zip: '',
  loan: { effectiveDate: '', formTypeId: 1, liabilityAmount: 0, overLimitApproval: false, grossPremium: 0, netPremium: 0, lenderName: '', borrowerName: '', scheduleB: '', loanNumber: '' },
  owner: { effectiveDate: '', formTypeId: 3, liabilityAmount: 0, overLimitApproval: false, grossPremium: 0, netPremium: 0 },
  commitment: { effectiveDate: '', formTypeId: 5, liabilityAmount: 0, grossPremium: 0 },
  mortgages: [],
  endorsements: [],
};

export default function JacketFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const [search] = useSearchParams();
  const id = params.id ? Number(params.id) : null;
  const isEdit = id !== null;
  const isUpload = search.get('upload') === '1';

  const { data: policyTypes = [] } = useQuery({ queryKey: ['jk-policy-types'], queryFn: () => api.getPolicyTypes() });
  const { data: propertyTypes = [] } = useQuery({ queryKey: ['jk-prop-types'], queryFn: () => api.getJacketPropertyTypes() });
  const { data: formTypes } = useQuery({ queryKey: ['jk-form-types'], queryFn: () => api.getJacketFormTypes() });
  const { data: counties = [] } = useQuery({ queryKey: ['jk-counties'], queryFn: () => api.getJacketCounties() });
  const { data: endorsements = [] } = useQuery({ queryKey: ['jk-endorsements'], queryFn: () => api.getJacketEndorsements() });
  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['jacket', id], queryFn: () => api.getJacket(id as number), enabled: isEdit,
  });

  const {
    register, handleSubmit, reset, watch, control, setValue, getValues,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const mortgageArray = useFieldArray({ control, name: 'mortgages' });
  // keyName '_key' so the endorsement's own numeric `id` (e.g. 111 = "Other")
  // isn't overwritten by react-hook-form's default 'id' field key.
  const endorsementArray = useFieldArray({ control, name: 'endorsements', keyName: '_key' });

  useEffect(() => {
    if (existing) {
      const { id: _id, void: _v, agentName: _a, ...values } = existing;
      void _id; void _v; void _a;
      reset(values);
    }
  }, [existing, reset]);

  // Select options load async; once present, re-push current values to the DOM
  // so each <select> shows the value the form actually holds (otherwise an
  // uncontrolled select shows the first option while the form holds the
  // default/record value). policyType has a "" placeholder so it's exempt. Runs once.
  const optionsSynced = useRef(false);
  useEffect(() => {
    if (optionsSynced.current) return;
    if (!formTypes || propertyTypes.length === 0 || counties.length === 0) return;
    optionsSynced.current = true;
    const v = getValues();
    setValue('propertyType', v.propertyType);
    setValue('county', v.county);
    setValue('loan.formTypeId', v.loan.formTypeId);
    setValue('owner.formTypeId', v.owner.formTypeId);
    setValue('commitment.formTypeId', v.commitment.formTypeId);
  }, [formTypes, propertyTypes, counties, getValues, setValue]);

  // Arrived from a scanned upload (?ocr=done): the backend already read the PDF
  // with Claude and filled this jacket synchronously, so just flag it for review.
  const ocrDone = search.get('ocr') === 'done';

  // New jacket only: on file-number blur, prefill empty property + borrower/lender
  // fields from a matching CPL (port of getProperty.php).
  const fileNumberReg = register('fileNumber');
  const policyTypeReg = register('policyType');
  const prefillFromCpl = async (fileNumber: string) => {
    if (isEdit) return;
    const fn = fileNumber.trim();
    if (!fn) return;
    try {
      const p = await api.getJacketPropertyByFile(fn);
      if (!p) return;
      const cur = getValues();
      if (p.address && !cur.address.trim()) setValue('address', p.address);
      if (p.city && !cur.city.trim()) setValue('city', p.city);
      if (p.county && !cur.county.trim()) setValue('county', p.county);
      if (p.zip && !cur.zip.trim()) setValue('zip', p.zip);
      if (p.buyer && !cur.loan.borrowerName.trim()) setValue('loan.borrowerName', p.buyer);
      if (p.lender && !cur.loan.lenderName.trim()) setValue('loan.lenderName', p.lender);
    } catch {
      // best-effort prefill — ignore failures
    }
  };

  // Auto-fill a section's Gross Premium from its Liability Amount using the OCA
  // rate table (same as the rate calculator). Recomputes whenever the amount changes.
  const fillPremium = async (premiumPath: string, amountStr: string) => {
    const amount = Number(String(amountStr).replace(/[$,]/g, '')) || 0;
    if (amount <= 0) return;
    const premium = await api.getJacketPremium(amount).catch(() => null);
    if (premium !== null) {
      setValue(premiumPath as Parameters<typeof setValue>[0], premium as never, { shouldDirty: true });
    }
  };

  const policyType = watch('policyType');
  const loanAmount = watch('loan.liabilityAmount');
  const ownerAmount = watch('owner.liabilityAmount');

  const showLoan = policyType === 'LM' || policyType === 'LM & LO';
  const showOwner = policyType === 'LO' || policyType === 'LM & LO' || policyType === 'EN';
  const showCommitment = policyType === 'CO';
  const allowMortgages = policyType !== '' && policyType !== 'CO';

  const onSubmit = async (values: Values) => {
    const payload = values as JacketFormValues;
    if (isEdit) await api.updateJacket(id as number, payload);
    else await api.createJacket(payload);
    // Force the list (and this record) to refetch so the edit shows without a
    // manual refresh (the list query has a 30s staleTime).
    await queryClient.invalidateQueries({ queryKey: ['jackets'] });
    if (isEdit) queryClient.invalidateQueries({ queryKey: ['jacket', id] });
    // Return to where they came from (the list, on the same page/filters).
    navigate(-1);
  };

  const addEndorsement = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const endId = Number(e.target.value);
    if (!endId) return;
    const found = endorsements.find((x) => x.id === endId);
    endorsementArray.append({ id: endId, name: found?.name ?? 'Other', price: 0 });
    e.target.value = '';
  };

  if (isEdit && loadingExisting) {
    return <div className="ast-cpl text-center py-5"><Spinner /></div>;
  }

  // New jacket from a scanned upload: the jacket doesn't exist yet, so show only
  // the uploader. It creates the jacket and navigates to its edit page on success.
  if (!isEdit && isUpload) {
    return (
      <div className="ast-cpl ast-cpl-form">
        <div className="ast-page-head">
          <h2 className="serif ast-page-title">New Jacket from Upload</h2>
        </div>
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Upload Scanned Jacket</h3>
          <p className="text-muted small">
            Upload a scanned jacket PDF. We'll create the jacket and open it so you can complete the details.
          </p>
          <JacketUpload />
        </section>
        <div className="ast-form-actions">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
            Back to Jackets
          </button>
        </div>
      </div>
    );
  }

  // Upload Images for an existing jacket: show the uploader and the list of
  // already-attached files — not the jacket detail form (matches the live app).
  if (isUpload && id != null) {
    return (
      <div className="ast-cpl ast-cpl-form">
        <div className="ast-page-head">
          <h2 className="serif ast-page-title">Upload Images</h2>
        </div>
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Add Document</h3>
          <p className="text-muted small">Attach scanned documents or images to this jacket.</p>
          <JacketUpload
            jacketId={id}
            onUploaded={() => queryClient.invalidateQueries({ queryKey: ['jacket-attachments', id] })}
          />
        </section>
        <JacketAttachments jacketId={id} />
        <div className="ast-form-actions">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(`/jackets/${id}/edit`)}>
            Edit Jacket Details
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
            Back to Jackets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ast-cpl ast-cpl-form">
      <div className="ast-page-head">
        <h2 className="serif ast-page-title">{!isEdit ? 'New Jacket' : 'Edit Jacket'}</h2>
      </div>

      {ocrDone && (
        <div className="alert alert-success py-2">
          <i className="bi bi-stars me-1" /> Fields below were filled from the uploaded document — please review and correct anything before saving.
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* File Details */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">File Details</h3>
          <div className="ast-grid-2">
            <FormField label="File Number" htmlFor="jk_file" error={errors.fileNumber?.message}>
              <input
                id="jk_file"
                className="form-control form-control-sm"
                {...fileNumberReg}
                onBlur={(e) => { void fileNumberReg.onBlur(e); void prefillFromCpl(e.currentTarget.value); }}
              />
            </FormField>
            <div />
            <FormField label="Jacket Selection" htmlFor="jk_policy" error={errors.policyType?.message}>
              <select
                id="jk_policy"
                className="form-select form-select-sm"
                {...policyTypeReg}
                onChange={(e) => {
                  void policyTypeReg.onChange(e);
                  // Simultaneous: the loan policy is a flat $50 add-on (no rate calc).
                  if (e.target.value === 'LM & LO') setValue('loan.grossPremium', 50);
                }}
              >
                <option value="">Select…</option>
                {policyTypes.map((p) => <option key={p.name} value={p.name}>{p.label}</option>)}
              </select>
            </FormField>
            <FormField label="Property Type" htmlFor="jk_proptype">
              <select id="jk_proptype" className="form-select form-select-sm" {...register('propertyType')}>
                {propertyTypes.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </FormField>
          </div>
        </section>

        {/* Property Information */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Property Information</h3>
          <div className="ast-grid-2">
            <FormField label="Address" htmlFor="jk_address">
              <input id="jk_address" className="form-control form-control-sm" {...register('address')} />
            </FormField>
            <FormField label="City" htmlFor="jk_city">
              <input id="jk_city" className="form-control form-control-sm" {...register('city')} />
            </FormField>
            <FormField label="County" htmlFor="jk_county">
              <select id="jk_county" className="form-select form-select-sm" {...register('county')}>
                {counties.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
            <FormField label="State" htmlFor="jk_state">
              <input id="jk_state" className="form-control form-control-sm" readOnly {...register('state')} />
            </FormField>
            <FormField label="Zip Code" htmlFor="jk_zip">
              <input id="jk_zip" className="form-control form-control-sm" maxLength={10} {...register('zip')} />
            </FormField>
          </div>
        </section>

        {/* Loan */}
        {showLoan && (
          <section className="ast-card ast-form-section">
            <h3 className="ast-section-title">Loan</h3>
            <div className="ast-grid-2">
              <FormField label="Effective Date" htmlFor="loan_date">
                <input id="loan_date" type="date" className="form-control form-control-sm" {...register('loan.effectiveDate')} />
              </FormField>
              <FormField label="Form Selection" htmlFor="loan_form">
                <select id="loan_form" className="form-select form-select-sm" {...register('loan.formTypeId')}>
                  {(formTypes?.LM ?? []).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </FormField>
              <FormField label="Liability Amount" htmlFor="loan_amt">
                <input id="loan_amt" type="number" step="0.01" className="form-control form-control-sm"
                  {...register('loan.liabilityAmount')}
                  onBlur={(e) => {
                    // Simultaneous loan premium is the flat $50 add-on — don't rate-calc it.
                    if (policyType !== 'LM & LO') void fillPremium('loan.grossPremium', e.currentTarget.value);
                  }} />
              </FormField>
              <FormField label="Over Limit Approval" htmlFor="loan_over">
                <div className={`form-check ${Number(loanAmount) >= OVER_LIMIT ? '' : 'invisible'}`}>
                  <input id="loan_over" className="form-check-input" type="checkbox" {...register('loan.overLimitApproval')} />
                  <label className="form-check-label" htmlFor="loan_over">Approved over $1M</label>
                </div>
              </FormField>
              <FormField label="Gross Premium" htmlFor="loan_gross">
                <input id="loan_gross" type="number" step="0.01" className="form-control form-control-sm" {...register('loan.grossPremium')} />
              </FormField>
              <FormField label="Net Premium" htmlFor="loan_net">
                <input id="loan_net" type="number" step="0.01" className="form-control form-control-sm" {...register('loan.netPremium')} />
              </FormField>
              <FormField label="Lender Name" htmlFor="loan_lender">
                <Controller name="loan.lenderName" control={control}
                  render={({ field }) => (
                    <LenderAutocomplete id="loan_lender" value={field.value} onChange={field.onChange} />
                  )} />
              </FormField>
              <FormField label="Borrower Name" htmlFor="loan_borrower">
                <input id="loan_borrower" className="form-control form-control-sm" {...register('loan.borrowerName')} />
              </FormField>
              <FormField label="Loan Number" htmlFor="loan_number">
                <input id="loan_number" className="form-control form-control-sm" {...register('loan.loanNumber')} />
              </FormField>
            </div>
          </section>
        )}

        {/* Owner */}
        {showOwner && (
          <section className="ast-card ast-form-section">
            <h3 className="ast-section-title">Owner</h3>
            <div className="ast-grid-2">
              <FormField label="Effective Date" htmlFor="own_date">
                <input id="own_date" type="date" className="form-control form-control-sm" {...register('owner.effectiveDate')} />
              </FormField>
              <FormField label="Form Selection" htmlFor="own_form">
                <select id="own_form" className="form-select form-select-sm" {...register('owner.formTypeId')}>
                  {(formTypes?.LO ?? []).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </FormField>
              <FormField label="Liability Amount" htmlFor="own_amt">
                <input id="own_amt" type="number" step="0.01" className="form-control form-control-sm"
                  {...register('owner.liabilityAmount')}
                  onBlur={(e) => void fillPremium('owner.grossPremium', e.currentTarget.value)} />
              </FormField>
              <FormField label="Over Limit Approval" htmlFor="own_over">
                <div className={`form-check ${Number(ownerAmount) >= OVER_LIMIT ? '' : 'invisible'}`}>
                  <input id="own_over" className="form-check-input" type="checkbox" {...register('owner.overLimitApproval')} />
                  <label className="form-check-label" htmlFor="own_over">Approved over $1M</label>
                </div>
              </FormField>
              <FormField label="Gross Premium" htmlFor="own_gross">
                <input id="own_gross" type="number" step="0.01" className="form-control form-control-sm" {...register('owner.grossPremium')} />
              </FormField>
              <FormField label="Net Premium" htmlFor="own_net">
                <input id="own_net" type="number" step="0.01" className="form-control form-control-sm" {...register('owner.netPremium')} />
              </FormField>
            </div>
          </section>
        )}

        {/* Commitment */}
        {showCommitment && (
          <section className="ast-card ast-form-section">
            <h3 className="ast-section-title">Commitment</h3>
            <div className="ast-grid-2">
              <FormField label="Effective Date" htmlFor="com_date">
                <input id="com_date" type="date" className="form-control form-control-sm" {...register('commitment.effectiveDate')} />
              </FormField>
              <FormField label="Form Selection" htmlFor="com_form">
                <select id="com_form" className="form-select form-select-sm" {...register('commitment.formTypeId')}>
                  {(formTypes?.CO ?? []).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </FormField>
              <FormField label="Liability Amount" htmlFor="com_amt">
                <input id="com_amt" type="number" step="0.01" className="form-control form-control-sm"
                  {...register('commitment.liabilityAmount')}
                  onBlur={(e) => void fillPremium('commitment.grossPremium', e.currentTarget.value)} />
              </FormField>
              <FormField label="Gross Premium" htmlFor="com_gross">
                <input id="com_gross" type="number" step="0.01" className="form-control form-control-sm" {...register('commitment.grossPremium')} />
              </FormField>
            </div>
          </section>
        )}

        {/* Additional Loans */}
        {allowMortgages && (
          <MortgagesSection
            control={control}
            register={register}
            fields={mortgageArray.fields}
            append={() => mortgageArray.append({ date: '', formTypeId: 1, amount: 0, grossPremium: 0, netPremium: 0, loanNumber: '', lenderName: '', borrowerName: '' })}
            remove={mortgageArray.remove}
            formTypes={(formTypes?.LM ?? [])}
            fillPremium={fillPremium}
          />
        )}

        {/* Endorsements */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Endorsements</h3>
          <select className="form-select form-select-sm mb-3" defaultValue="" onChange={addEndorsement}>
            <option value="">Select endorsements…</option>
            {endorsements.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          {endorsementArray.fields.length === 0 && <div className="text-muted small">No endorsements added.</div>}
          {endorsementArray.fields.map((f, i) => (
            <div className="ast-end-row" key={f._key}>
              {f.id === 111 ? (
                <input className="form-control form-control-sm" placeholder="Endorsement name" {...register(`endorsements.${i}.name`)} />
              ) : (
                <span className="ast-end-name">{f.name}</span>
              )}
              <input type="number" step="0.01" className="form-control form-control-sm ast-end-price"
                placeholder="0.00" {...register(`endorsements.${i}.price`)} />
              <button type="button" className="btn btn-sm btn-outline-danger" title="Remove"
                onClick={() => endorsementArray.remove(i)}>
                <i className="bi bi-x-lg" />
              </button>
            </div>
          ))}
        </section>

        {/* Actions */}
        <div className="ast-form-actions">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Back</button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => { reset(EMPTY); setValue('policyType', ''); }}>Clear</button>
          <button type="submit" className="btn btn-ast-blue" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isEdit ? 'Update' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---- Additional Loans subsection ---------------------------------------- */
interface MortgagesSectionProps {
  control: Control<Values>;
  register: UseFormRegister<Values>;
  fields: { id: string }[];
  append: () => void;
  remove: (i: number) => void;
  formTypes: { id: number; label: string }[];
  fillPremium: (premiumPath: string, amountStr: string) => void;
}
function MortgagesSection({ control, register, fields, append, remove, formTypes, fillPremium }: MortgagesSectionProps) {
  return (
    <section className="ast-card ast-form-section">
      <h3 className="ast-section-title">Additional Loans</h3>
      {fields.length === 0 && <div className="text-muted small mb-3">No additional loans.</div>}
      {fields.map((f, i) => (
        <div className="ast-mortgage-row" key={f.id}>
          <div className="ast-mortgage-head">
            <span className="fw-semibold">Loan {i + 1}</span>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(i)} title="Remove loan">
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="ast-grid-2">
            <FormField label="Policy Effective Date" htmlFor={`m_date_${i}`}>
              <input id={`m_date_${i}`} type="date" className="form-control form-control-sm" {...register(`mortgages.${i}.date`)} />
            </FormField>
            <FormField label="Form Selection" htmlFor={`m_form_${i}`}>
              <select id={`m_form_${i}`} className="form-select form-select-sm" {...register(`mortgages.${i}.formTypeId`)}>
                {formTypes.map((ft) => <option key={ft.id} value={ft.id}>{ft.label}</option>)}
              </select>
            </FormField>
            <FormField label="Liability Amount" htmlFor={`m_amt_${i}`}>
              <input id={`m_amt_${i}`} type="number" step="0.01" className="form-control form-control-sm"
                {...register(`mortgages.${i}.amount`)}
                onBlur={(e) => fillPremium(`mortgages.${i}.grossPremium`, e.currentTarget.value)} />
            </FormField>
            <FormField label="Gross Premium" htmlFor={`m_gross_${i}`}>
              <input id={`m_gross_${i}`} type="number" step="0.01" className="form-control form-control-sm" {...register(`mortgages.${i}.grossPremium`)} />
            </FormField>
            <FormField label="Net Premium" htmlFor={`m_net_${i}`}>
              <input id={`m_net_${i}`} type="number" step="0.01" className="form-control form-control-sm" {...register(`mortgages.${i}.netPremium`)} />
            </FormField>
            <FormField label="Loan Number" htmlFor={`m_loan_${i}`}>
              <input id={`m_loan_${i}`} className="form-control form-control-sm" {...register(`mortgages.${i}.loanNumber`)} />
            </FormField>
            <FormField label="Lender Name" htmlFor={`m_lender_${i}`}>
              <Controller name={`mortgages.${i}.lenderName`} control={control}
                render={({ field }) => (
                  <LenderAutocomplete id={`m_lender_${i}`} value={field.value} onChange={field.onChange} />
                )} />
            </FormField>
            <FormField label="Borrower Name" htmlFor={`m_borrower_${i}`}>
              <input id={`m_borrower_${i}`} className="form-control form-control-sm" {...register(`mortgages.${i}.borrowerName`)} />
            </FormField>
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary w-100" onClick={append}>
        <i className="bi bi-plus-lg me-1" />Add Loan
      </button>
    </section>
  );
}
